import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, Receipt, AlertTriangle, TrendingUp, ShoppingCart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/StatCard";
import { SalesChart } from "@/components/SalesChart";
import { api, formatRupiah, PAYMENT_LABEL } from "@/lib/api";

const LowStockList = ({ items }) => (
  <div className="rounded-2xl border border-amber-200 bg-white p-5" data-testid="dashboard-low-stock-panel">
    <div className="mb-4 flex items-center gap-2">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <h3 className="font-display text-lg font-semibold text-slate-800">Stok Menipis</h3>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-slate-500" data-testid="dashboard-low-stock-empty">Semua stok aman.</p>
    ) : (
      <ul className="divide-y divide-slate-100">
        {items.map((p) => (
          <li key={p.id} className="flex items-center justify-between py-2.5" data-testid={`dashboard-low-stock-item-${p.id}`}>
            <div>
              <p className="text-sm font-medium text-slate-800">{p.name}</p>
              <p className="text-xs text-slate-500">Min. {p.min_stock}</p>
            </div>
            <span className={`rounded-full px-2.5 py-1 font-mono text-xs font-bold ${p.stock === 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
              {p.stock === 0 ? "Habis" : `Sisa ${p.stock}`}
            </span>
          </li>
        ))}
      </ul>
    )}
    <Link to="/produk?filter=low" className="mt-4 inline-block text-sm font-semibold text-amber-700 hover:underline" data-testid="dashboard-restock-link">
      Kelola stok &rarr;
    </Link>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data));
  }, []);

  if (!data) return <p className="text-slate-500" data-testid="dashboard-loading">Memuat dashboard...</p>;
  const top = data.week.top_products[0];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">Ringkasan Toko</h1>
          <p className="mt-1 text-sm text-slate-500">{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        <Button asChild className="bg-emerald-600 hover:bg-emerald-700" data-testid="dashboard-open-kasir-button">
          <Link to="/kasir"><ShoppingCart className="mr-2 h-4 w-4" /> Buka Kasir</Link>
        </Button>
      </div>

      {data.low_stock.length > 0 && (
        <div className="rise flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="dashboard-low-stock-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><b>{data.low_stock.length} produk</b> stoknya menipis. Segera lakukan restok.</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Penjualan Hari Ini" value={formatRupiah(data.today.revenue)} icon={Wallet} testId="stat-today-revenue" sub={`Laba ${formatRupiah(data.today.profit)}`} />
        <StatCard label="Transaksi Hari Ini" value={data.today.count} icon={Receipt} tone="sky" testId="stat-today-count" sub={`${data.today.items_sold} item terjual`} delay={60} />
        <StatCard label="Stok Menipis" value={data.low_stock.length} icon={AlertTriangle} tone="amber" testId="stat-low-stock" sub={`dari ${data.product_count} produk`} delay={120} />
        <StatCard label="Terlaris (7 hari)" value={top ? top.qty : 0} icon={TrendingUp} tone="slate" testId="stat-top-product" sub={top ? top.name : "Belum ada penjualan"} delay={180} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-2">
          <div className="mb-2 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold text-slate-800">Penjualan 7 Hari Terakhir</h3>
            <span className="font-mono text-sm font-bold text-emerald-700" data-testid="dashboard-week-revenue">{formatRupiah(data.week.revenue)}</span>
          </div>
          <SalesChart data={data.series} testId="dashboard-sales-chart" />
        </div>
        <LowStockList items={data.low_stock} />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-4 font-display text-lg font-semibold text-slate-800">Metode Pembayaran Hari Ini</h3>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(data.today.payment_methods).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-slate-50 p-4" data-testid={`dashboard-payment-${k}`}>
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{PAYMENT_LABEL[k]}</p>
              <p className="mt-1 font-mono text-sm font-bold text-slate-900 md:text-base">{formatRupiah(v)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
